import { FlowConfiguration } from "../FlowConfiguration";
import { FlowStepData } from "../Steps/FlowStepData";
import { FlowConstants } from "./FlowConstants";
import { FlowStepOutputInstructionType } from "../Structure/FlowStepOutputInstructions";
import { DataDefinition } from "../../Common/Data/DataDefinition";
import { DataUtilities } from "../../Common/DataUtilities";
import { IDataDefinition } from "../../Common/Data/Definitions/IDataDefinition";

export interface StepWithPath {
    step: FlowStepData;
    path?: string;
}

export class StepNode {
    parents: { parent: StepNode; path?: string }[] = [];
    step: FlowStepData;
    children: { path?: string; step: StepNode }[] = [];

    constructor(step: FlowStepData) {
        this.step = step;
    }
}

export class StepNodes {
    flow: FlowConfiguration;
    nodes: { [id: string]: StepNode } = {};

    constructor(flow: FlowConfiguration) {
        this.flow = flow;
    }

    static buildNodes(flow: FlowConfiguration): StepNodes {
        let nodes = new StepNodes(flow);

        let toCheck: {
            step: FlowStepData;
            parentNode?: StepNode;
            path?: string;
        }[] = [];

        toCheck.push({ step: flow.getPrimarySequence()?.allSteps[0] });
        toCheck.push({
            step: flow.getSequence(FlowConstants.ERROR_SEQUENCE)?.allSteps[0] as FlowStepData,
        });
        toCheck.push({
            step: flow.getSequence(FlowConstants.END_SEQUENCE)?.allSteps[0] as FlowStepData,
        });

        while (toCheck.length !== 0) {
            let checking = toCheck.shift();

            if (!checking) continue;

            let step = checking.step;

            let node = nodes.nodes[step._id] ?? new StepNode(step);
            if (checking.parentNode) {
                if (!checking.path) {
                    checking.parentNode.children.push({ step: node });
                    node.parents.push({ parent: checking.parentNode });
                } else {
                    checking.parentNode.children.push({
                        step: node,
                        path: checking.path,
                    });
                    node.parents.push({
                        parent: checking.parentNode,
                        path: checking.path,
                    });
                }
            }

            nodes.nodes[step._id] = node;

            if (step.getOutcomeInstructions().length === 0) {
                // evaluate next step in stack if any
                let nextStep = step.sequence?.getStepAfter(step, true);

                if (nextStep) {
                    toCheck.push({ step: nextStep, parentNode: node });
                }
            } else {
                for (let p of step.getOutcomeInstructions()) {
                    switch (p.strategy) {
                        case FlowStepOutputInstructionType.BRANCH:
                            let seqToBranch = flow.getSequence(p.connectedSequenceId!);

                            if (seqToBranch) {
                                for (let s of seqToBranch.branches) {
                                    let nextStepBranch = s.steps.length === 0 ? undefined : s.steps[0];

                                    if (nextStepBranch) {
                                        toCheck.push({
                                            step: nextStepBranch,
                                            parentNode: node,
                                            path: p.pathName,
                                        });
                                    }
                                }
                            }

                            break;
                        case FlowStepOutputInstructionType.CONTINUE:
                            let nextStepContinue = step.sequence?.getStepAfter(step, true);

                            if (nextStepContinue) {
                                toCheck.push({
                                    step: nextStepContinue,
                                    parentNode: node,
                                    path: p.pathName,
                                });
                            }
                            break;
                        case FlowStepOutputInstructionType.THROW_EXCEPTION:
                            let nextStepError = flow.getSequence(FlowConstants.ERROR_SEQUENCE)?.allSteps[0];

                            if (nextStepError) {
                                toCheck.push({
                                    step: nextStepError,
                                    parentNode: node,
                                    path: p.pathName,
                                });
                            }
                            break;
                        case FlowStepOutputInstructionType.END_FLOW:
                            if (step.sequence?._id === FlowConstants.END_SEQUENCE) break;

                            let nextStepEnd = flow.getSequence(FlowConstants.END_SEQUENCE)?.allSteps[0];

                            if (nextStepEnd) {
                                toCheck.push({
                                    step: nextStepEnd,
                                    parentNode: node,
                                    path: p.pathName,
                                });
                            }
                            break;
                    }
                }
            }
        }

        return nodes;
    }

    // eslint-disable-next-line
    invalidateAfter(step: string) {
        this.nodes = StepNodes.buildNodes(this.flow).nodes;
    }

    invalidate() {
        this.nodes = StepNodes.buildNodes(this.flow).nodes;
    }

    getStepsAfter(step: string): StepWithPath[] {
        let output: StepWithPath[] = [];

        let myNode = this.nodes[step];

        let toCheck: { step: StepNode; path?: string }[] = [];
        for (let p of myNode.children) {
            toCheck.push(p);
        }

        while (toCheck.length > 0) {
            let checking = toCheck.shift();

            if (!checking) continue;

            output.push({ step: checking!.step.step, path: checking!.path });
            for (let p of checking!.step.children) {
                toCheck.push(p);
            }
        }

        return output;
    }

    getStepsBefore(step: string): StepWithPath[] {
        let output: StepWithPath[] = [];

        let myNode = this.nodes[step];

        if (!myNode) {
            throw new Error("could not find step " + step);
        }

        let toCheck: { parent: StepNode; path?: string }[] = [];
        if (myNode.parents) {
            for (let p of myNode.parents) {
                toCheck.push(p);
            }
        }

        while (toCheck.length > 0) {
            let checking = toCheck.shift();

            if (checking && checking.parent) {
                output.push({ step: checking.parent.step, path: checking!.path });
                if (checking.parent.parents) {
                    for (let p of checking.parent.parents) {
                        toCheck.push(p);
                    }
                }
            }
        }

        return output;
    }
}

export enum FlowDataFetchType {
    All,
    Changed,
    Added,
}

export interface StepWithPath {
    step: FlowStepData;
    path?: string;
}

export class FlowUtilities {
    static IsEndStep(step: FlowStepData) {
        return Reflect.get(step, "hiddenEndStep");
    }

    static getDataForSequence(flow: FlowConfiguration, sequenceId: string): DataDefinition {
        let seq = flow.getSequence(sequenceId);

        if (!seq) throw new Error("sequence not found");

        return this.getDataAtStep(flow, seq.allSteps[0], false);
    }

    static getDataAtStep(flow: FlowConfiguration, step: FlowStepData, addSequenceData: boolean = true): DataDefinition {
        let inputDataDef = flow.getInputDefinition();
        let stepWithPaths = this.getStepsBefore(flow, step);

        let datadef = new DataDefinition();
        datadef.fields = inputDataDef.fields;

        for (let s of stepWithPaths) {
            // steps without paths cannot produce data
            if (!s.path) continue;

            let matchedOutcome = s.step.getOutcomes().find((o) => o.name === s.path);

            if (matchedOutcome) {
                for (let ofield of matchedOutcome.dataOutput) {
                    // check if field is existed. for now, just igore if there is a field with same name.
                    // todo: add validation for changed field

                    try {
                        let field = DataUtilities.getFieldFromDataDefinitionByName(
                            datadef.fields,
                            ofield.name
                        ) as IDataDefinition;

                        if (!field) {
                            datadef.fields.push(ofield);
                        }
                    } catch (e) {
                        datadef.fields.push(ofield);
                    }
                }
            }
        }

        // add data only for sequence we are in
        if (addSequenceData && step.sequence?.getAddedData() && step.sequence?.getAddedData()?.length !== 0) {
            for (let d of step.sequence!.getAddedData()!) {
                datadef.fields.push(d);
            }
        }

        // add all the data the end sequence has
        if (step.sequence?._id === FlowConstants.ERROR_SEQUENCE || step.sequence?._id == FlowConstants.END_SEQUENCE) {
            for (let s of FlowUtilities.getAllSteps(flow)) {
                if (
                    s.sequence?._id !== FlowConstants.ERROR_SEQUENCE &&
                    s.sequence?._id !== FlowConstants.END_SEQUENCE
                ) {
                    if (s.getOutcomes()) {
                        for (let o of s.getOutcomes()) {
                            for (let d of o.dataOutput) {
                                if (!datadef.fields.find((def) => def.name == d.name)) datadef.fields.push(d);
                            }
                        }
                    }
                }
            }
        }

        return datadef;
    }

    static getStepsBefore(flow: FlowConfiguration, step: FlowStepData): StepWithPath[] {
        return flow.nodes.getStepsBefore(step._id);
    }

    static getAllSteps(flow: FlowConfiguration): FlowStepData[] {
        let output: FlowStepData[] = [];

        for (let s of flow.allSequences) {
            output.push(...s.allSteps);
        }

        return output;
    }

    static getFlowDataDefinitions(flow: FlowConfiguration, type: FlowDataFetchType) {
        let variables: string[] = [];
        let allData: IDataDefinition[] = [];
        let inputDataNames: string[] = [];

        inputDataNames.push(
            ...flow.getInputDefinition().fields.map((d) => {
                return d.name;
            })
        );

        allData.push(...flow.getInputDefinition().fields);

        if (type === FlowDataFetchType.All) {
            variables.push(...inputDataNames);
        }

        for (let s of this.getAllSteps(flow)) {
            let outcomes = s.getOutcomes();
            if (!outcomes) {
            } else {
                for (let o of outcomes) {
                    if (o.dataOutput) {
                        for (let d of o.dataOutput) {
                            let n = d.name;
                            if (n.indexOf(".") !== -1) {
                                n = n.split(".")[0];
                                // data already exists
                            } else {
                                if (!allData.find((ddd) => ddd.name === n)) allData.push(d);
                            }

                            if (type === FlowDataFetchType.Added && inputDataNames.indexOf(n) !== 0) {
                                // this data was in input data, so was not added.
                            } else {
                                if (variables.indexOf(n) !== -1) variables.push(n);
                            }
                        }
                    }
                }
            }
        }

        let output: IDataDefinition[] = [];

        for (let v of variables) {
            let dd = DataUtilities.getFieldFromDataDefinitionByName(allData, v);

            if (dd) output.push(dd);
        }

        return output;
    }
}
